import { add } from 'date-fns'
import React from 'react'

import { FilterActions, InitialFilterState, useFiltersContext } from '@/context'
import useHomeReadinessSection from '@/hooks/useDashboard/useHomeReadinessSection'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { HomeQualityCheckStatus } from '@/types/HomeQualityCheck'

import { Card } from './Card'

const HomeReadiness = () => {
  const upcomingQuery = useHomeReadinessSection()
  const upcomingData = upcomingQuery?.data?.agmDashboard
  const navigate = useGuardedNavigate()
  const [, FilterDispatch] = useFiltersContext()

  const handleMoveIns = () => {
    FilterDispatch(
      FilterActions.setFilters({
        filterName: 'reservations',
        filters: {
          ...InitialFilterState['reservationsFilters'],
          moveIn7Days: true,
          approved: false,
        },
      }),
    )
    FilterDispatch(FilterActions.clearReservationMoveOutRange())
    navigate('/reservations')
  }

  const handleMoveOuts = () => {
    const today = new Date()
    const sevenDays = add(new Date(), { days: 7 })
    FilterDispatch(
      FilterActions.setFilters({
        filterName: 'reservations',
        filters: {
          ...InitialFilterState['reservationsFilters'],
        },
      }),
    )
    FilterDispatch(FilterActions.setReservationMoveOutRange({ r: { moveOutDate: today, moveOutMax: sevenDays } }))
    navigate('/reservations')
  }

  const handleFlightXReservations = () => {
    FilterDispatch(
      FilterActions.setFilters({
        filterName: 'reservations',
        filters: {
          ...InitialFilterState['reservationsFilters'],
          approved: false,
          flightxInProgress: true,
        },
      }),
    )
    FilterDispatch(FilterActions.clearReservationMoveOutRange())
    navigate('/reservations')
  }

  const handleLexitReservations = () => {
    FilterDispatch(
      FilterActions.setFilters({
        filterName: 'reservations',
        filters: {
          ...InitialFilterState['reservationsFilters'],
          approved: false,
          lexit: true,
        },
      }),
    )
    FilterDispatch(FilterActions.clearReservationMoveOutRange())
    navigate('/reservations')
  }

  const handleDeinstallHomes = () => {
    FilterDispatch(
      FilterActions.setFilters({
        filterName: 'homes',
        filters: {
          ...InitialFilterState['homesFilters'],
          scheduledForDeinstall: true,
        },
      }),
    )
    navigate('/homes')
  }

  const handlePendingReservation = () => {
    FilterDispatch(
      FilterActions.setFilters({
        filterName: 'reservations',
        filters: {
          ...InitialFilterState['reservationsFilters'],
          approved: false,
          moveIn3days: true,
          pending: true,
        },
      }),
    )
    FilterDispatch(FilterActions.clearReservationMoveOutRange())
    navigate('/reservations')
  }

  const handleHomesQualityChecks = (homeQualityCheckStatus: HomeQualityCheckStatus, filterByDate: boolean) => {
    const today = filterByDate ? new Date() : undefined
    const sevenDays = filterByDate ? add(new Date(), { days: 7 }) : undefined
    FilterDispatch(
      FilterActions.setFilters({
        filterName: 'homeQualityChecks',
        filters: {
          homeQualityCheckStatus,
        },
      }),
    )
    FilterDispatch(FilterActions.setReservationMoveInRange({ r: { moveInDate: today, moveInMax: sevenDays } }))
    navigate('/homeQualityChecks')
  }

  return (
    <>
      <h2 className="my-4 text-xl">Home Readiness</h2>
      <div className="grid grid-cols-3 grid-rows-1 place-items-stretch gap-3 md:gap-6">
        <Card
          label="Move Ins"
          loading={upcomingQuery?.isLoading}
          onClick={handleMoveIns}
          subLabel="(Next 7 days)"
          value={upcomingData?.totalMoveIns || 0}
        />
        <Card
          label="Upcoming Move-Outs"
          loading={upcomingQuery?.isLoading}
          onClick={handleMoveOuts}
          subLabel="(Next 7 days)"
          value={upcomingData?.totalUpcomingMoveOuts || 0}
        />
        <Card
          label="Pending Verifications"
          loading={upcomingQuery?.isLoading}
          onClick={handlePendingReservation}
          subLabel="(Next 3 days)"
          value={upcomingData?.totalUpcomingReservationsPendingApproval || 0}
        />
        <Card
          label="Pending Inspections"
          loading={upcomingQuery?.isLoading}
          onClick={() => handleHomesQualityChecks(HomeQualityCheckStatus.NOT_READY, false)}
          value={upcomingData?.totalPendingInspections || 0}
        />
        <Card
          label="Homes Not Ready"
          loading={upcomingQuery?.isLoading}
          onClick={() => handleHomesQualityChecks(HomeQualityCheckStatus.NOT_READY, true)}
          subLabel="(Next 7 days)"
          value={upcomingData?.totalHomesNotReady || 0}
        />
        <Card
          label="Homes Ready"
          loading={upcomingQuery?.isLoading}
          onClick={() => handleHomesQualityChecks(HomeQualityCheckStatus.READY, true)}
          subLabel="(Next 7 days)"
          value={upcomingData?.totalHomesReady || 0}
        />
        <Card
          label="Upcoming De-Installs"
          loading={upcomingQuery?.isLoading}
          onClick={handleDeinstallHomes}
          value={upcomingData?.totalFutureDeinstalls || 0}
        />
        <Card
          label="Flight Xs"
          loading={upcomingQuery?.isLoading}
          onClick={handleFlightXReservations}
          subLabel="(Next 7 days)"
          value={upcomingData?.totalFxReservations || 0}
        />
        <Card
          label="Lexit"
          loading={upcomingQuery?.isLoading}
          onClick={handleLexitReservations}
          subLabel="(Current or Upcoming)"
          value={upcomingData?.totalLexitReservations || 0}
        />
      </div>
    </>
  )
}

export default HomeReadiness
